// Kopie z FofrLamaPOS (hooks/usePlatnoMeritko.js) — editor stolů je v pokladně i ve Správě OS
// (Správa → Provozovna → Stoly, 2026-10-20). Měň obojí stejně.
import { useEffect, useRef, useState } from 'react'

/**
 * Minimální dotyková plocha dlaždice po přepočtu měřítkem (px).
 *
 * Původních 88×72 bylo moc: na běžné pokladně 1366×768 vyjdou stoly kolem
 * 72×65 px a půdorys se celý přepnul na seznam, přestože se do dlaždic dá
 * pohodlně trefit. 56×44 drží doporučené minimum pro dotyk a seznam nechává
 * jen pro opravdu malé displeje.
 */
export const MIN_DOTYK = { sirka: 56, vyska: 44 }

/**
 * Přepočet vnitřních souřadnic plátna na obrazovku.
 *
 * Plátno je **pevná mřížka**, ne rozměr v pixelech: souřadnice stolů i sekcí
 * jsou v jejích jednotkách a nikdy se nemění. Na obrazovku se roztáhne tak,
 * aby ji vyplnilo celou — proto dvě měřítka, pro každou osu vlastní. Pokladny
 * mají různá rozlišení a poměry stran a s jedním společným měřítkem by na
 * každé zbýval jinde pruh prázdna; rozvržení postavené na jedné obrazovce by
 * na druhé sedělo jinak.
 *
 * `meritko` (menší z obou) zůstává pro věci, které se roztahovat nesmí:
 * písmo, tloušťky a rozteč mřížky. Ty se s plátnem zvětšují a zmenšují, ale
 * nedeformují.
 *
 * Vrací i `dotykoveOk`: když by nejmenší dlaždice klesla pod dotykový
 * minimál, má se místo půdorysu ukázat seznamový fallback.
 */
export function usePlatnoMeritko(zona, dlazdice) {
  const ref = useRef(null)
  const [rozmer, setRozmer] = useState({ sirka: 0, vyska: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const zmer = () => setRozmer({ sirka: el.clientWidth, vyska: el.clientHeight })
    zmer()

    const ro = new ResizeObserver(zmer)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const platnoSirka = zona?.sirka || 900
  const platnoVyska = zona?.vyska || 600

  const meritkoX = rozmer.sirka > 0 ? rozmer.sirka / platnoSirka : 0
  const meritkoY = rozmer.vyska > 0 ? rozmer.vyska / platnoVyska : 0
  const meritko = Math.min(meritkoX, meritkoY)

  const nejmensi = (dlazdice ?? []).reduce(
    (min, s) => ({
      sirka: Math.min(min.sirka, s.pozice?.sirka ?? Infinity),
      vyska: Math.min(min.vyska, s.pozice?.vyska ?? Infinity)
    }),
    { sirka: Infinity, vyska: Infinity }
  )

  const dotykoveOk =
    meritko === 0 ||
    nejmensi.sirka === Infinity ||
    (nejmensi.sirka * meritkoX >= MIN_DOTYK.sirka && nejmensi.vyska * meritkoY >= MIN_DOTYK.vyska)

  return { ref, meritko, meritkoX, meritkoY, platnoSirka, platnoVyska, dotykoveOk, rozmer }
}
